<script setup lang="ts">
import type { LibraryFiltersModel } from '@/js/account/entities/document/model/types';
import { ref, watch } from 'vue';
import BaseCheckbox from '@/js/account/components/base/BaseCheckbox.vue';
import BaseLoadState from '@/js/account/components/base/BaseLoadState.vue';
import { useLibraryFilterOptions } from '@/js/account/entities/document/model/use-library-filter-options';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    modelValue?: LibraryFiltersModel
  }>(),
  {
    modelValue: () => ({
      categoryIds: [],
      tagIds: [],
    }),
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: LibraryFiltersModel]
}>();

const cn = classNames('library-filters-panel');
const { categories, tags, loading: optionsLoading } = useLibraryFilterOptions();

type FilterKey = 'categoryIds' | 'tagIds';

function emptyFilters(): LibraryFiltersModel {
  return {
    categoryIds: [],
    tagIds: [],
  };
}

const draft = ref<LibraryFiltersModel>(emptyFilters());

function copyFilters(value: LibraryFiltersModel): LibraryFiltersModel {
  return {
    categoryIds: [...value.categoryIds],
    tagIds: [...value.tagIds],
  };
}

function setDraftSelection(
  key: FilterKey,
  id: number,
  selected: boolean,
): void {
  const ids = draft.value[key];

  draft.value[key] = selected
    ? ids.includes(id)
      ? ids
      : [...ids, id]
    : ids.filter(item => item !== id);
}

function toggleDraftSelection(key: FilterKey, id: number): void {
  setDraftSelection(key, id, !draft.value[key].includes(id));
}

function commitDraft(): LibraryFiltersModel {
  const value = copyFilters(draft.value);
  emit('update:modelValue', value);
  return value;
}

function resetDraft(): LibraryFiltersModel {
  const value = emptyFilters();
  draft.value = value;
  emit('update:modelValue', value);
  return value;
}

watch(
  () => props.modelValue,
  (value) => {
    draft.value = copyFilters(value);
  },
  { deep: true, immediate: true },
);

defineExpose({
  commitDraft,
  resetDraft,
});
</script>

<template>
  <div :class="cn()">
    <div :class="cn('section')">
      <p :class="cn('section-title')">
        Category
      </p>

      <BaseLoadState
        :loading="optionsLoading"
        :empty="categories.length === 0"
        empty-message="No categories available"
      >
        <ul :class="cn('category-list')">
          <li
            v-for="category in categories"
            :key="category.id"
            :class="cn('category-item')"
          >
            <BaseCheckbox
              :name="`category-${category.id}`"
              :model-value="draft.categoryIds.includes(category.id)"
              @update:model-value="
                setDraftSelection('categoryIds', category.id, $event)
              "
            >
              {{ category.name }}
            </BaseCheckbox>
          </li>
        </ul>
      </BaseLoadState>
    </div>

    <hr :class="cn('divider')">

    <div :class="cn('section')">
      <p :class="cn('section-title')">
        Tags
      </p>

      <BaseLoadState
        :loading="optionsLoading"
        :empty="tags.length === 0"
        empty-message="No tags available"
      >
        <div :class="cn('tag-list')">
          <button
            v-for="tag in tags"
            :key="tag.id"
            type="button"
            :class="cn('tag', { selected: draft.tagIds.includes(tag.id) })"
            @click="toggleDraftSelection('tagIds', tag.id)"
          >
            <span :class="cn('tag-label')">{{ tag.name }}</span>
            <span
              v-if="draft.tagIds.includes(tag.id)"
              :class="cn('tag-remove')"
              aria-hidden="true"
            >
              ×
            </span>
          </button>
        </div>
      </BaseLoadState>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.library-filters-panel {
  display: flex;
  flex-direction: column;

  &__divider {
    margin: 16px 0;
    border: none;
    border-top: 1px solid var(--base-field-border-color, #e0e0e0);
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__section-title {
    margin: 0;
    color: var(--color-text-primary);
    font-family: var(--font-family-sans);
    font-weight: var(--font-weight-regular);
    line-height: 1.2;
    font-size: 16px;
  }

  &__category-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__category-item {
    color: var(--color-text-secondary, #4c4c4c);
    font-size: 14px;
    font-family: var(--font-family-sans);
    line-height: 1.2;
  }

  &__tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__tag {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding: 4px 10px;
    color: var(--color-text-secondary, #4c4c4c);
    font-weight: 400;
    font-size: 12px;
    font-family: var(--font-family-sans);
    line-height: 1.2;
    background-color: var(--color-background-secondary, #73805b12);
    border: none;
    border-radius: 6px;
    cursor: pointer;

    &_selected {
      color: #fff;
      background-color: var(--color-primary, #73805b);
    }
  }

  &__tag-remove {
    font-size: 14px;
    line-height: 1;
  }
}
</style>
